// Deliveries related store state and actions.
import axiosApiClient from '@/api/axiosApiClient'
import router from '@/router'
import { getValueByPath, getTimezone } from '@/utils/helpers'
import { OrderEventFormSubmissionMap } from '@/utils/constants'
import log from '@/utils/log'
import _ from 'lodash'
import moment from 'moment-timezone'

// State
export const state = {
	list: [], // Deliveries list.
	currentId: '', // Currently searched ID.
	loading: false, // Deliveries loading state.
	error: null // Deliveries request error.
}

// Getters
export const getters = {}

// Mutations
export const mutations = {
	SET_DELIVERIES(state, newValues) {
		state.list = newValues
	},
	SET_CURRENT_ID(state, newValue) {
		state.currentId = newValue
	},
	SET_LOADING_DELIVERIES(state, newValue) {
		state.loading = newValue
	},
	SET_DELIVERIES_ERROR(state, newValue) {
		state.error = newValue
	}
}

// Actions
export const actions = {
	// Fetches deliveries.
	async fetchDeliveries(
		{ commit, rootState },
		{ clientRef = '', status = '' }
	) {
		commit('SET_DELIVERIES_ERROR', null)
		commit('SET_LOADING_DELIVERIES', true)
		commit('SET_CURRENT_ID', clientRef)
		if (router.history.current.query.search !== clientRef) {
			router.push({ name: 'Deliveries', query: { search: clientRef } })
		}
		let params = {
			clientId: rootState.auth.currentUser.clientId, // picked-up by API
			clientRef: `*${clientRef}*`, // Searched delivery ID
			limit: 25,
			checkInterim: true
		}

		if (status) {
			// For the future..
			// Fedor: tell me when the future comes
			params.status = status
		}

		try {
			let list = []
			const response = await axiosApiClient.get('deliveries', { params })
			list = response.data
			const orderRefs = []
			const deliveryIds = []
			response.data.forEach(i => {
				deliveryIds.push(i.id)
				const refs = getValueByPath(i, 'order.clientRefs', null)
				if (refs) {
					const orderRef = refs.find(o => o.type === 'ORDER')
					if (orderRef && !orderRefs.includes(orderRef.value)) {
						orderRefs.push(orderRef.value)
					}
				}
			})
			await Promise.all(
				orderRefs.map(async ref => {
					params = {
						clientId: rootState.auth.currentUser.clientId, // picked-up by API
						clientRef: `${ref}*`, // Searched delivery ID
						limit: 25,
						checkInterim: true
					}
					try {
						const response = await axiosApiClient.get(
							'deliveries',
							{
								params
							}
						)
						const deliveries = response.data
						deliveries.forEach(i => {
							if (!deliveryIds.includes(i.id)) {
								deliveryIds.push(i.id)
								list.push(i)
							}
						})
					} catch (e) {
						log.error(
							'Failed to fetch deliveries',
							{ category: 'API' },
							e.stack
						)
					}
				})
			)
			const groupedDeliveries = []
			const deliveriesWithOrder = {}

			list.forEach(item => {
				item.reason = ''
				item.recipient = 'N/A'
				if (item.order) {
					// Get additional info from order:
					const { address, contacts, pickupAt, status } = item.order
					let pickupContacts = []
					if (address[0].contacts) {
						// 1st address (pickup) is always available
						// Get contacts from new address format:
						pickupContacts = address[0].contacts
					} else {
						// Fallback to root-level contacts for backward compatibility:
						pickupContacts = contacts
					}
					let dropoffContacts = []
					if (address[1] && address[1].contacts) {
						// 2nd address (dropoff) is optional
						dropoffContacts = address[1].contacts
					}
					item.pickupAt = pickupAt
					item.sender = pickupContacts[0]
						? pickupContacts[0].name
						: ''
					item.senderPhone = pickupContacts[0]
						? pickupContacts[0].phone
						: ''
					item.recipient = dropoffContacts[0]
						? dropoffContacts[0].name
						: ''
					item.recipientPhone = dropoffContacts[0]
						? dropoffContacts[0].phone
						: ''
					item.pickupAddress = address[0].text // 1st address (pickup) is always available
					item.dropoffAddress = address[1] ? address[1].text : '' // 2nd address (dropoff) is optional
					item.orderStatus = status
				}
				const fsLength = item.formSubmissions.length
				if (fsLength) {
					const lastSubmission = item.formSubmissions[fsLength - 1]
					// UpdatedAt is the last form-submission's submittedAt.
					item.updatedAt = lastSubmission.submittedAt

					const reason = getReason(lastSubmission)
					if (reason) {
						item.reason = reason
					}

					const dropOffSuccess = item.formSubmissions.find(
						fs => fs.tag === 'DROP_OFF_SUCCESS'
					)
					if (dropOffSuccess) {
						// Get recipient.
						const recipientField = dropOffSuccess.fields.find(
							field => field.tag === 'RECIPIENT'
						)
						if (recipientField) {
							item.recipient = recipientField.values[0]
						}
					}
				}
				item.clientRef = {}
				item.clientRef.orders = item.clientRefs
					.filter(ref => ref.type === 'ORDER')
					.map(ref => ref.value)
				item.clientRef.parcels = item.clientRefs
					.filter(ref => ref.type === 'PARCEL')
					.map(ref => ref.value)
				item.courier = item.courier ? item.courier.info.name : ''
				item.logs = createLogs(item) // Construct delivery logs.

				const orderId = (item.order || {}).id
				if (orderId) {
					const orderRef = item.clientRef.orders[0]
					const orderId = item.order.id
					if (orderRef) {
						deliveriesWithOrder[`${orderRef}-${orderId}`] = (
							deliveriesWithOrder[`${orderRef}-${orderId}`] || []
						).concat([item])
						return
					}
					deliveriesWithOrder[orderId] = (
						deliveriesWithOrder[orderId] || []
					).concat([item])
					return
				}
				groupedDeliveries.push(item)
			})

			for (const [, values] of Object.entries(deliveriesWithOrder)) {
				const order = (values.find(value => value.order) || {}).order
				const orderEvents = await getOrderEvents(order.id)
				const timezone = getTimezone(
					rootState.config.locations,
					order.locationId
				)
				const lastUpdatedTime = computeOrderLastUpdatedTime(
					values,
					orderEvents
				)
				const deliveriesWithTimezone = values.map(value => ({
					...value,
					timezone
				}))
				groupedDeliveries.push({
					orderId: _.get(values, '0.clientRef.orders[0]', ''),
					values: mergeDuplicatedDeliveries(deliveriesWithTimezone),
					status: _.get(values, '0.orderStatus'),
					clientId: values[0] ? values[0].clientId : '',
					orderEvents: orderEvents.map(event => {
						const deliveryId = _.get(
							event,
							'payload.parcelRefs.0.deliveryId'
						)
						const formSubmissionLocationId = _.chain(values)
							.find({
								id: deliveryId
							})
							.get('formSubmissions')
							.find({
								tag: OrderEventFormSubmissionMap[event.type]
							})
							.get('locationId')
							.value()
						return {
							...event,
							timezone: formSubmissionLocationId
								? getTimezone(
										rootState.config.locations,
										formSubmissionLocationId
								  )
								: timezone
						}
					}),
					timezone,
					lastUpdatedTime
				})
			}
			commit('SET_DELIVERIES', groupedDeliveries)
			commit('SET_LOADING_DELIVERIES', false)
		} catch (error) {
			commit('SET_DELIVERIES', [])
			commit('SET_LOADING_DELIVERIES', false)
			commit('SET_DELIVERIES_ERROR', error)
			log.error(
				'Failed to fetch deliveries',
				{ category: 'API' },
				error.stack
			)
		}
	},

	// Clears deliveries list.
	clearDeliveries({ commit }) {
		commit('SET_DELIVERIES_ERROR', null)
		commit('SET_CURRENT_ID', '')
		commit('SET_DELIVERIES', [])
		if (router.history.current.query.search) {
			router.push({ name: 'Deliveries' })
		}
	}
}

// Constructs delivery logs from order and form-submissions.
function createLogs({ order, formSubmissions, ...item }) {
	const logs = []
	if (item.deliveryRequests && item.deliveryRequests.length > 0) {
		// only show 'Delivery Requested' once
		const earliestDeliveryRequest = _.minBy(
			item.deliveryRequests,
			'createdAt'
		)
		const { createdAt, orderId } = earliestDeliveryRequest
		logs.push({
			id: `ORDER-${orderId}`,
			tag: 'ORDER',
			datetime: createdAt // Display delivery creation time.
		})
	} else if (order) {
		logs.push({
			id: `ORDER-${order.id}`,
			tag: 'ORDER',
			datetime: item.createdAt // Display delivery creation time.
		})
	}
	if (formSubmissions) {
		formSubmissions.forEach(fs => {
			const reason = getReason(fs)

			const remark = _.chain(fs)
				.get('fields')
				.find({ tag: 'REMARKS' })
				.get('values.0')
				.replace(/\n/g, ' ')
				.value()

			const aliases = getValueByPath(fs, 'user.aliases', [])
			logs.push({
				id: `${fs.tag}-${fs.id}`,
				tag: fs.tag,
				reason,
				remark,
				datetime: fs.submittedAt, // Display form submission time.
				courierName: fs.user ? fs.user.info.name : '',
				courierPhone: aliases.length ? (aliases[0] || {}).id : '',
				// In case we want to hide certain from-submissions (e.g. REVIEWING/REJECTED cross-dock, inline form-submissions).
				hidden: fs.fields.find(
					f => f.tag === 'ROOT_FORM_SUBMITTER_REF'
				),
				clientId: _.get(fs, 'clientId.0', ''),
				proofsOfDelivery: getProofsOfDelivery(fs.fields)
			})
		})
	}
	// sort by datetime (formSubmission.submittedAt):
	return logs.sort((a, b) => new Date(a.datetime) - new Date(b.datetime))
}

async function getOrderEvents(orderId) {
	try {
		if (!orderId) {
			return null
		}
		return _.get(
			await axiosApiClient.get(`/order-events/${orderId}`),
			'data'
		)
	} catch (e) {
		log.error(e)
	}
}

/**
 * get the most updated time among events, deliveries and order,
 *
 * @param {{updatedAt: Date, order: {updatedAt} | null}[]} deliveries
 * @param {{createdAt: Date}[]} events
 * @returns {Date}
 */
function computeOrderLastUpdatedTime(deliveries = [], events = []) {
	const dates = [
		..._.map(deliveries, 'updatedAt'),
		_.get(deliveries, '0.order.updatedAt', null),
		..._.map(events, 'createdAt')
	]
	return moment.max(dates.map(d => moment(d)))
}

function mergeDuplicatedDeliveries(deliveries) {
	const deliveriesGroupedByClientRef = _.groupBy(
		deliveries,
		'clientRef.parcels.0'
	)
	const result = []
	for (const [_clientRef, groupedDeliveries] of _.toPairs(
		deliveriesGroupedByClientRef
	)) {
		const delivery = { ...groupedDeliveries[0] }
		delivery.formSubmissions = _.chain(groupedDeliveries)
			.map('formSubmissions')
			.flatten()
			.uniqBy('id')
			.value()
		// for generating delivery log "Delivery Requested"
		delivery.deliveryRequests = _.map(groupedDeliveries, delivery => ({
			orderId: delivery.order ? delivery.order.id : '',
			createdAt: delivery.createdAt
		}))
		delivery.logs = createLogs(delivery)
		result.push(delivery)
	}
	return result
}

/**
 * Return urls of POD if form submissions have fields with type PHOTO/SIGNATURE
 *
 * @param {{tag: string,values: string[],type?: string}[]} formSubmissionFields
 * @returns {string[]}
 */
function getProofsOfDelivery(formSubmissionFields = []) {
	const validFieldTypeOrTag = ['PHOTO', 'SIGNATURE', 'BARCODE']

	/** @type {{tag: string,values: string[], type?: string}[]} */
	const fieldsWithPhoto = _.filter(formSubmissionFields, ({ type, tag }) => {
		return (
			validFieldTypeOrTag.includes(type) ||
			/PHOTO|SIGNATURE|BARCODE/i.test(tag)
		)
	})

	fieldsWithPhoto.sort(field => {
		return field.type === 'SIGNATURE' || /SIGNATURE/i.test(field.tag)
			? 1
			: -1
	})

	/** @type {(string|undefined[])[]} */
	const result = _.map(fieldsWithPhoto, field => {
		if (field.type === 'BARCODE' || /BARCODE/i.test(field.tag)) {
			return _.get(field, 'meta.0.0.frame')
		}
		return field.values
	})

	return _.flatten(result).filter(Boolean)
}

/**
 * Get reason field for display
 * @param {*} formSubmission
 * @returns {string | null}
 */
function getReason(formSubmission) {
	// currently this are the only forms that has reasonsField might need to add here in case we will support new form with reasons
	const formTagsWithReasons = [
		'PICKUP_LAST_MILE',
		'PICKUP_FIRST_MILE',
		'PICKUP_FAIL',
		'DROP_OFF_FAILURE',
		'DROP_OFF_SUCCESS',
		'DROP_OFF_WAREHOUSE',
		'CROSSDOCK_DROP_OFF'
	]
	if (formTagsWithReasons.includes(formSubmission.tag)) {
		const reasonField = formSubmission.fields.find(
			field =>
				field.tag.includes('REASONS_SUCCESS_PICKUP') ||
				field.tag.includes('REASONS_FAILURE_PICKUP') ||
				field.tag.includes('REASONS_FAILURE') ||
				field.tag.includes('REASONS_SUCCESS') ||
				field.tag.includes('REASONS_WAREHOUSE') ||
				field.tag.includes('REASONS_CROSSDOCK_DROP_OFF')
		)
		if (reasonField) {
			return reasonField.values[0]
		}
	}
	return null
}
